import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { LifeStatus } from '@/api/type/user';
import type { Member } from '@/types';
import { EDUCATION_QUALIFICATION_OPTIONS } from '@/lib/constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getEducationQualificationLabel = (value?: string | null) =>
  EDUCATION_QUALIFICATION_OPTIONS.find((option) => option.label.toLowerCase() === value?.trim().toLowerCase())?.label ??
  (value?.trim() || undefined);

export function formatFileName(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  const hasExtension = lastDot > 0;
  const baseName = hasExtension ? filename.slice(0, lastDot) : filename;
  const extension = hasExtension ? filename.slice(lastDot) : '';

  if (baseName.length <= 15) {
    return filename;
  }

  return `${baseName.slice(0, 10)}.....${baseName.slice(-5)}${extension}`;
}

export const getLifeStatus = (member: Pick<Member, 'lifeStatus'>): LifeStatus =>
  member.lifeStatus === 'deceased' ? 'deceased' : 'alive';

export const SUCCESS_NOTICE_CLASS =
  'rounded-lg border border-teal-100/90 bg-teal-50/50 px-4 py-3 text-sm text-gray-700';

export const SUCCESS_NOTICE_ACCENT_CLASS = 'text-teal-800';

// Built with the RegExp constructor so the unicode flag works with the current TS target.
const NON_LETTER_PATTERN = new RegExp('[^\\p{L}\\s]', 'gu');

/** Keeps letters (any language) and spaces only, e.g. for names. */
export const toLettersOnly = (value: string) => value.replace(NON_LETTER_PATTERN, '');

/** Numbers are shown with at least 3 digits: 4 → '004', 1201 → '1201'. */
export const toThreeDigits = (value: string | number): string => String(value).padStart(3, '0');

/** The member number staff see (member_id, already 3+ digits); the internal id (user_id) is only for API calls. */
export const displayMemberNo = (member: { id: string; memberNo?: string }): string =>
  member.memberNo ?? toThreeDigits(member.id);

/** "#9 - Meena Lakshmi", used in drawer and popup headers. */
export const memberHeading = (member: { id: string; name: string; memberNo?: string }): string =>
  `#${displayMemberNo(member)} - ${member.name}`;
